import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle, XCircle, Clock, CaretRight, Microphone, StopCircle, MagicWand } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";

function StatusPill({ status }) {
  const cls = {
    pending: "bg-amber-50 text-amber-700",
    approved: "bg-emerald-50 text-emerald-700",
    rejected: "bg-red-50 text-[#D0021B]",
    cancelled: "bg-zinc-100 text-zinc-500",
  }[status] || "bg-zinc-100";
  return <span className={`text-[10px] font-mono px-1.5 py-0.5 ${cls}`}>{status.toUpperCase()}</span>;
}

function RunRow({ run, onAction, isMine }) {
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const decide = async (action) => {
    setBusy(true);
    try {
      await onAction(run.id, action, comment);
      setComment("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-zinc-200 hover:border-zinc-950 transition-colors p-4" data-testid={`run-${run.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-[10px] tracking-[0.15em] text-zinc-500 uppercase">{run.resource_type.replace("_", " ")}</span>
            <StatusPill status={run.status} />
          </div>
          <div className="font-semibold">{run.resource_label || run.resource_id}</div>
          {run.resource_amount !== null && run.resource_amount !== undefined && (
            <div className="font-display font-black text-lg tnum mt-0.5">${run.resource_amount.toLocaleString()}</div>
          )}
          <div className="font-mono text-[10px] text-zinc-400 mt-1">
            Template: {run.template_name} · Initiated by {run.initiated_by}
          </div>
        </div>
      </div>

      <div className="mt-4 space-y-1.5">
        {run.steps.map((s, i) => {
          const isCurrent = i === run.current_step && run.status === "pending";
          const isPast = !!s.action;
          return (
            <div key={`${run.id}-step-${i}-${s.name}`} className={`flex items-center gap-2 text-xs ${isCurrent ? "text-zinc-950 font-semibold" : isPast ? "text-zinc-600" : "text-zinc-400"}`}>
              {s.action === "approve" ? <CheckCircle size={12} className="text-emerald-600" /> :
               s.action === "reject" ? <XCircle size={12} className="text-[#D0021B]" /> :
               isCurrent ? <Clock size={12} className="text-amber-600 pulse-dot" /> :
               <CaretRight size={12} />}
              <span>{s.name}</span>
              {s.actor_email && <span className="font-mono text-[10px] text-zinc-400">· {s.actor_email}</span>}
              {s.comment && <span className="italic text-zinc-500">&ldquo;{s.comment}&rdquo;</span>}
            </div>
          );
        })}
      </div>

      {isMine && run.status === "pending" && (
        <div className="mt-4 border-t border-zinc-100 pt-3">
          <input
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Optional comment"
            className="w-full border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-1.5 text-sm outline-none mb-2"
            data-testid={`run-comment-${run.id}`}
          />
          <div className="flex gap-2">
            <button onClick={() => decide("approve")} disabled={busy} className="bg-emerald-700 text-white text-xs font-semibold px-4 py-1.5 hover:bg-emerald-800 disabled:opacity-50" data-testid={`run-approve-${run.id}`}>APPROVE</button>
            <button onClick={() => decide("reject")} disabled={busy} className="border border-[#D0021B] text-[#D0021B] text-xs font-semibold px-4 py-1.5 hover:bg-[#D0021B] hover:text-white disabled:opacity-50" data-testid={`run-reject-${run.id}`}>REJECT</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Approvals() {
  const [inbox, setInbox] = useState([]);
  const [history, setHistory] = useState([]);
  const [view, setView] = useState("inbox");

  const refresh = useCallback(async () => {
    try {
      const [i, h] = await Promise.all([
        api.get("/workflows/inbox").then((r) => r.data),
        api.get("/workflows/runs").then((r) => r.data),
      ]);
      setInbox(i); setHistory(h);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("Approvals load failed", err);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const decide = async (runId, action, comment) => {
    try {
      await api.post(`/workflows/runs/${runId}/${action}`, { comment });
      toast.success(`Run ${action === "approve" ? "approved" : "rejected"}`);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const list = view === "inbox" ? inbox : history;

  return (
    <div className="p-6 sm:p-8 max-w-4xl" data-testid="approvals-page">
      <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-400">MODULE / 12 · APPROVALS</div>
      <h1 className="font-display text-4xl font-black tracking-tighter mt-1">Approvals.</h1>

      <VoiceApprovalPanel onCommitted={refresh} />

      <div className="flex gap-px bg-zinc-200 border border-zinc-200 mt-6 w-fit">
        <button onClick={() => setView("inbox")} className={`px-4 py-2 text-xs font-mono tracking-wider ${view === "inbox" ? "bg-zinc-950 text-white" : "bg-white text-zinc-600 hover:text-zinc-950"}`} data-testid="approvals-view-inbox">
          INBOX ({inbox.length})
        </button>
        <button onClick={() => setView("history")} className={`px-4 py-2 text-xs font-mono tracking-wider ${view === "history" ? "bg-zinc-950 text-white" : "bg-white text-zinc-600 hover:text-zinc-950"}`} data-testid="approvals-view-history">
          ALL RUNS ({history.length})
        </button>
      </div>

      <div className="mt-4 space-y-3">
        {list.length === 0 ? (
          <div className="text-xs text-zinc-400 font-mono py-6">{view === "inbox" ? "INBOX EMPTY — NOTHING NEEDS YOUR APPROVAL" : "NO RUNS YET"}</div>
        ) : (
          list.map((r) => <RunRow key={r.id} run={r} onAction={decide} isMine={view === "inbox"} />)
        )}
      </div>
    </div>
  );
}

// ============================================================
// Voice-driven Approvals (uses OpenAI Whisper via backend)
// ============================================================
function VoiceApprovalPanel({ onCommitted }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Microphone not supported in this browser");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      mediaRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 500) { toast.error("Recording too short — try again"); return; }
        setBusy(true);
        const fd = new FormData();
        fd.append("audio", blob, "voice-approval.webm");
        try {
          const r = await api.post("/workflows/approvals/voice", fd, { headers: { "Content-Type": "multipart/form-data" } });
          setPreview(r.data);
          toast.success("Transcribed — review preview below");
        } catch (err) {
          toast.error(formatApiError(err.response?.data?.detail) || "Transcription failed");
        } finally { setBusy(false); }
      };
      mr.start();
      setRecording(true);
    } catch (err) {
      toast.error(`Cannot start recording: ${err.message}`);
    }
  };

  const stop = () => {
    if (mediaRef.current && recording) {
      mediaRef.current.stop();
      setRecording(false);
    }
  };

  const commit = async () => {
    if (!preview?.matched_run || !preview.action) return;
    setBusy(true);
    try {
      const r = await api.post("/workflows/approvals/voice/commit", {
        run_id: preview.matched_run.id,
        action: preview.action,
        transcript: preview.transcript,
      });
      toast.success(`Run ${r.data.action === "approve" ? "approved" : "rejected"} by voice`);
      setPreview(null);
      onCommitted?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 border border-zinc-950 bg-zinc-50" data-testid="voice-approval-panel">
      <div className="bg-zinc-950 text-white px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Microphone size={14} weight="fill" className="text-[#FF3E00]" />
          <span className="font-mono text-[10px] tracking-widest">VOICE APPROVAL · say "approve invoice INV-2026-000045"</span>
        </div>
      </div>
      <div className="p-3 flex flex-wrap items-center gap-3">
        {!recording ? (
          <button
            type="button"
            onClick={start}
            disabled={busy}
            className="flex items-center gap-2 bg-zinc-950 text-white px-3 py-1.5 text-xs font-semibold hover:bg-[#FF3E00] disabled:opacity-40"
            data-testid="voice-approval-start"
          >
            <Microphone size={12} weight="fill" /> START
          </button>
        ) : (
          <button
            type="button"
            onClick={stop}
            className="flex items-center gap-2 bg-[#D0021B] text-white px-3 py-1.5 text-xs font-semibold pulse-dot"
            data-testid="voice-approval-stop"
          >
            <StopCircle size={12} weight="fill" /> STOP
          </button>
        )}
        {busy && <span className="text-xs font-mono text-zinc-500 pulse-dot">TRANSCRIBING…</span>}
      </div>
      {preview && (
        <div className="border-t border-zinc-200 p-3 space-y-2 bg-white" data-testid="voice-approval-preview">
          <div className="text-xs italic text-zinc-700 border-l-2 border-zinc-950 pl-2" data-testid="voice-approval-transcript">"{preview.transcript}"</div>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className={preview.action ? "border border-emerald-400 bg-emerald-50 p-2" : "border border-zinc-200 p-2"}>
              <div className="font-mono text-[9px] tracking-wider text-zinc-500">ACTION</div>
              <div className="font-semibold uppercase" data-testid="voice-approval-action">{preview.action || "—"}</div>
            </div>
            <div className={preview.invoice_no ? "border border-emerald-400 bg-emerald-50 p-2" : "border border-zinc-200 p-2"}>
              <div className="font-mono text-[9px] tracking-wider text-zinc-500">INVOICE</div>
              <div className="font-mono" data-testid="voice-approval-invoice-no">{preview.invoice_no || preview.run_id || "—"}</div>
            </div>
            <div className={preview.matched_run ? "border border-emerald-400 bg-emerald-50 p-2" : "border border-red-200 bg-red-50 p-2"}>
              <div className="font-mono text-[9px] tracking-wider text-zinc-500">MATCH</div>
              <div className="font-semibold" data-testid="voice-approval-match">{preview.matched_run ? "FOUND" : "NONE"}</div>
            </div>
          </div>
          <div className="text-xs text-zinc-500">{preview.hint}</div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={commit}
              disabled={!preview.confirmable || busy}
              className="flex items-center gap-1 bg-zinc-950 text-white px-3 py-1.5 text-xs font-semibold hover:bg-[#FF3E00] disabled:opacity-40"
              data-testid="voice-approval-commit"
            >
              <MagicWand size={12} weight="fill" /> COMMIT {preview.action?.toUpperCase()}
            </button>
            <button type="button" onClick={() => setPreview(null)} className="text-xs font-mono text-zinc-500 hover:text-zinc-950" data-testid="voice-approval-discard">Discard</button>
          </div>
        </div>
      )}
    </div>
  );
}

